import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Badge, type BadgeProps, Button, Dialog, Headline } from "@fronterahealth/frontera-ui-components";

import {
  PayorTypeEnum,
  type TemplateDetail as TemplateDetailType,
  deleteTemplate,
  exportTemplateJson,
  importTemplateJson,
  promoteTemplate,
  updateTemplate,
  useGetTemplateDetail,
} from "@api/rest/types-and-hooks";
import { AssignOrgsDialog } from "@components/Templates/AssignOrgsDialog";
import { PublishDialog } from "@components/Templates/PublishDialog";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { TemplateQARulesTab } from "@pages/QARules/TemplateQARulesTab";
import { PromptReportTypeMismatchDialog } from "@pages/Templates/PromptReportTypeMismatchDialog";
import { RioChatDrawer } from "@pages/Templates/RioChatDrawer";
import { TemplateDetails } from "@pages/Templates/TemplateDetails";
import { TemplatePromptSandbox } from "@pages/Templates/TemplatePromptSandbox";
import { TemplateQACriteria } from "@pages/Templates/TemplateQACriteria";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";
import { TemplateStructure } from "@pages/Templates/TemplateStructure";
import {
  ExportFramework,
  ReportType,
  ReviewSettingChoice,
  type ReviewSettingChoiceValue,
  TemplateStatus,
  reviewSettingChoiceFromValue,
  reviewSettingValueFromChoice,
} from "@pages/Templates/constants";
import { useAllPrompts } from "@pages/Templates/hooks/useAllPrompts";
import { convertDisplayPlaceholdersToKeys, convertKeyPlaceholdersToDisplay } from "@pages/Templates/placeholders";
import {
  type PromptReportTypeMismatch,
  detailsSaveChecksPrompts,
  promptMismatchesForDetailsSave,
} from "@pages/Templates/promptReportType";
import { resolveUnifiedExport } from "@pages/Templates/resolveUnifiedExport";
import { type TemplateType, flagsFromTemplateType, templateTypeFromFlags } from "@pages/Templates/templateType";

const StatusBadge: Record<string, BadgeProps["appearance"]> = {
  Draft: "neutral",
  Published: "success",
  Archived: "error",
};

enum TemplateTab {
  Structure = "structure",
  Details = "details",
  QACriteria = "qa-criteria",
  QARules = "qa-rules",
  PromptSandbox = "prompt-sandbox",
}

const TAB_LABELS: Record<TemplateTab, string> = {
  [TemplateTab.Details]: "Details",
  [TemplateTab.Structure]: "Structure",
  [TemplateTab.QACriteria]: "QA Criteria (legacy)",
  [TemplateTab.QARules]: "QA Rules",
  [TemplateTab.PromptSandbox]: "Evaluations",
};

export const TemplateDetail: FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const go = useNavigate();
  const queryClient = useQueryClient();
  const numericId = Number(templateId);

  const [searchParams] = useSearchParams();
  // `?tab=` deep-links to a tab (e.g. QA Rules from a package's "Used by" list).
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<TemplateTab>(
    Object.values(TemplateTab).find((tab) => tab === tabParam) ?? TemplateTab.Structure,
  );
  const [structureDirty, setStructureDirty] = useState(false);
  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false);
  const pendingAction = useRef<TemplateTab | "back" | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [editOrgsOpen, setEditOrgsOpen] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editReportType, setEditReportType] = useState("");
  const [editExportFramework, setEditExportFramework] = useState("");
  const [editTemplateUrl, setEditTemplateUrl] = useState("");
  const [editHeaderText, setEditHeaderText] = useState("");
  const [editFooterText, setEditFooterText] = useState("");
  const [editFont, setEditFont] = useState<string | null>(null);
  const [editUnifiedExport, setEditUnifiedExport] = useState(false);
  // Radio-style: a template is exactly one of these three. Backend stores
  // it as (is_base_template, enterprise_template) — see
  // ``templateTypeFromFlags``/``flagsFromTemplateType`` in ./templateType.
  const [editTemplateType, setEditTemplateType] = useState<TemplateType>("self_service");
  const [editRequireAllQaResolved, setEditRequireAllQaResolved] = useState(false);
  const [editRequireApproval, setEditRequireApproval] = useState<ReviewSettingChoiceValue>(ReviewSettingChoice.INHERIT);
  const [editFinalApproverWaits, setEditFinalApproverWaits] = useState<ReviewSettingChoiceValue>(
    ReviewSettingChoice.INHERIT,
  );
  const [editPayorType, setEditPayorType] = useState<PayorTypeEnum | null>(null);
  const [editDefaultPayorTemplate, setEditDefaultPayorTemplate] = useState(false);
  const [urlError, setUrlError] = useState("");
  // The list outlives the open flag so the dialog keeps its text while it
  // animates closed.
  const [promptMismatches, setPromptMismatches] = useState<PromptReportTypeMismatch[]>([]);
  const [promptMismatchDialogOpen, setPromptMismatchDialogOpen] = useState(false);
  const { data: allPrompts, isLoading: isPromptsLoading } = useAllPrompts();

  const {
    data: detailData,
    isLoading,
    refetch,
  } = useGetTemplateDetail(numericId, {
    query: { queryKey: ["template", numericId], enabled: !!numericId },
  });

  const template: TemplateDetailType | undefined = detailData?.status === 200 ? detailData.data : undefined;
  const isStructureReadOnly = template?.status === TemplateStatus.PUBLISHED;
  const hasUnsavedChanges = isEditing || structureDirty;

  const handleTabClick = (tab: TemplateTab) => {
    if (
      !isStructureReadOnly &&
      activeTab === TemplateTab.Structure &&
      structureDirty &&
      tab !== TemplateTab.Structure
    ) {
      pendingAction.current = tab;
      setUnsavedWarningOpen(true);
    } else {
      setActiveTab(tab);
    }
  };

  const handleBackToTemplates = () => {
    if (!isStructureReadOnly && activeTab === TemplateTab.Structure && structureDirty) {
      pendingAction.current = "back";
      setUnsavedWarningOpen(true);
    } else {
      go("/templates");
    }
  };

  const handleConfirmLeave = () => {
    setUnsavedWarningOpen(false);
    setStructureDirty(false);
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action === "back") {
      go("/templates");
    } else if (action) {
      setActiveTab(action);
    } else if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancelLeave = () => {
    setUnsavedWarningOpen(false);
    pendingAction.current = null;
    if (blocker.state === "blocked") blocker.reset();
  };

  // Block browser-level navigation only when structure has unsaved changes on an editable template
  const blocker = useBlocker(!isStructureReadOnly && activeTab === TemplateTab.Structure && structureDirty);

  useEffect(() => {
    if (blocker.state === "blocked") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnsavedWarningOpen(true);
    }
  }, [blocker.state]);

  // Set to true before a file-upload refetch so the sync below doesn't overwrite the
  // framework the user has selected in the still-open Export Settings dialog.
  const preserveExportFrameworkRef = useRef(false);

  useEffect(() => {
    if (!template || isEditing) return;
    if (!preserveExportFrameworkRef.current) {
      setEditExportFramework(template.export_framework ?? "");
      setEditHeaderText(convertKeyPlaceholdersToDisplay(template.header_text ?? ""));
      setEditFooterText(convertKeyPlaceholdersToDisplay(template.footer_text ?? ""));
      setEditFont(template.export_template_font ?? null);
      setEditUnifiedExport(template.unified_export ?? false);
    }
    preserveExportFrameworkRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEditTemplateUrl(template.template_url ?? "");
  }, [template, isEditing]);

  const startEdit = () => {
    if (!template) return;
    setEditName(template.name);
    setEditDescription(template.description ?? "");
    setEditStatus(template.status);
    setEditReportType(template.report_type ?? "");
    setEditExportFramework(template.export_framework ?? "");
    setEditTemplateUrl(template.template_url ?? "");
    setEditHeaderText(convertKeyPlaceholdersToDisplay(template.header_text ?? ""));
    setEditFooterText(convertKeyPlaceholdersToDisplay(template.footer_text ?? ""));
    setEditFont(template.export_template_font ?? null);
    setEditUnifiedExport(template.unified_export ?? false);
    setEditTemplateType(
      templateTypeFromFlags(template.is_base_template ?? false, template.enterprise_template ?? false),
    );
    setEditRequireAllQaResolved(template.require_all_qa_resolved ?? false);
    setEditRequireApproval(reviewSettingChoiceFromValue(template.require_approval_before_export));
    setEditFinalApproverWaits(reviewSettingChoiceFromValue(template.final_approver_waits_for_reviewers));
    setEditPayorType(template.payor_type ?? null);
    setEditDefaultPayorTemplate(template.default_payor_template ?? false);
    setUrlError("");
    setDetailsDirty(false);
    setIsEditing(true);
  };

  // Wraps a setter so any field change marks the form dirty
  const markDirty =
    <T,>(fn: (v: T) => void) =>
    (v: T) => {
      setDetailsDirty(true);
      fn(v);
    };

  const validateAndSave = () => {
    if (editReportType === ReportType.REASSESSMENT && editExportFramework === ExportFramework.GOOGLE_DOCS) {
      const msg = "Reassessment templates cannot use Google Docs export.";
      setUrlError(msg);
      notifyError(msg);
      return;
    }
    if (
      ([ExportFramework.GOOGLE_DOCS, ExportFramework.DOCXTEMPLATER] as string[]).includes(editExportFramework) &&
      !editTemplateUrl.trim()
    ) {
      const msg = "A template URL is required for this export framework.";
      setUrlError(msg);
      notifyError(msg);
      return;
    }
    if (editExportFramework === ExportFramework.GOOGLE_DOCS && editTemplateUrl && editTemplateUrl.includes("/edit")) {
      const msg = "Google Docs template URL should not contain '/edit'. Use the base document URL.";
      setUrlError(msg);
      notifyError(msg);
      return;
    }
    setUrlError("");
    // Only a save that switches type or publishes needs the prompt list.
    const edits = { reportType: editReportType, status: editStatus };
    if (template && detailsSaveChecksPrompts(template, edits) && isPromptsLoading) {
      notifyError("Still checking this template's AI prompts. Try saving again in a moment.");
      return;
    }
    const mismatches = template ? promptMismatchesForDetailsSave(template, edits, allPrompts ?? []) : [];
    if (mismatches.length > 0) {
      setPromptMismatches(mismatches);
      setPromptMismatchDialogOpen(true);
      return;
    }
    updateMutation.mutate();
  };

  // Saves only export settings fields, preserving all other template fields from server state.
  // Used by the Export Settings dialog so it works without requiring the main Edit button.
  // Returns true if validation passed and the mutation was fired (so the dialog can close).
  const saveExportSettings = (): boolean => {
    if (!template) return false;
    if (editReportType === ReportType.REASSESSMENT && editExportFramework === ExportFramework.GOOGLE_DOCS) {
      const msg = "Reassessment templates cannot use Google Docs export.";
      setUrlError(msg);
      notifyError(msg);
      return false;
    }
    if (editExportFramework === ExportFramework.GOOGLE_DOCS && !editTemplateUrl.trim()) {
      setUrlError("A template URL is required for Google Docs.");
      return false;
    }
    if (editExportFramework === ExportFramework.GOOGLE_DOCS && editTemplateUrl && editTemplateUrl.includes("/edit")) {
      setUrlError("Google Docs template URL should not contain '/edit'. Use the base document URL.");
      return false;
    }
    setUrlError("");
    exportSettingsMutation.mutate();
    return true;
  };

  const exportSettingsMutation = useMutation({
    mutationFn: () =>
      updateTemplate({
        template_id: numericId,
        name: template?.name ?? editName,
        description: template?.description ?? editDescription ?? null,
        status: template?.status ?? editStatus,
        report_type: template?.report_type ?? editReportType ?? null,
        export_framework: editExportFramework || null,
        template_url: editTemplateUrl || null,
        header_text: convertDisplayPlaceholdersToKeys(editHeaderText) || null,
        footer_text: convertDisplayPlaceholdersToKeys(editFooterText) || null,
        export_template_font: editFont || null,
        unified_export: editUnifiedExport,
        is_base_template: template?.is_base_template ?? false,
        enterprise_template: template?.enterprise_template ?? false,
        payor_type: template?.payor_type ?? editPayorType,
        default_payor_template: template?.default_payor_template ?? editDefaultPayorTemplate,
        require_all_qa_resolved: template?.require_all_qa_resolved ?? editRequireAllQaResolved,
      }),
    onSuccess: (result) => {
      if (result.status === 200) {
        notifySuccess("Export settings saved");
        setUrlError("");
        void refetch();
        void queryClient.invalidateQueries({ queryKey: ["templates"] });
      } else {
        notifyError("Failed to save export settings");
      }
    },
    onError: () => notifyError("Failed to save export settings"),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateTemplate({
        template_id: numericId,
        name: editName,
        description: editDescription || null,
        status: editStatus,
        report_type: editReportType || null,
        export_framework: editExportFramework || null,
        template_url: editTemplateUrl || null,
        header_text: convertDisplayPlaceholdersToKeys(editHeaderText) || null,
        footer_text: convertDisplayPlaceholdersToKeys(editFooterText) || null,
        export_template_font: editFont || null,
        unified_export: editUnifiedExport,
        ...flagsFromTemplateType(editTemplateType),
        payor_type: editPayorType,
        default_payor_template: editDefaultPayorTemplate,
        require_all_qa_resolved: editRequireAllQaResolved,
        require_approval_before_export: reviewSettingValueFromChoice(editRequireApproval),
        final_approver_waits_for_reviewers: reviewSettingValueFromChoice(editFinalApproverWaits),
      }),
    onSuccess: (result) => {
      if (result.status !== 200) {
        notifyError("Failed to update template");
        return;
      }
      notifySuccess("Template updated");
      setIsEditing(false);
      setDetailsDirty(false);
      setUrlError("");
      void refetch();
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: () => notifyError("Failed to update template"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTemplate({ template_id: numericId }),
    onSuccess: (result) => {
      if (result.status !== 200) {
        notifyError("Failed to delete template");
        return;
      }
      notifySuccess("Template deleted");
      go("/templates");
    },
    onError: () => notifyError("Failed to delete template"),
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const exportRes = await exportTemplateJson(numericId, { cache: "no-store" });
      if (exportRes.status !== 200) throw new Error("export failed");
      const jsonStr = (exportRes.data as unknown as { json_data: string })?.json_data ?? "";
      const parsed = JSON.parse(jsonStr) as { name: string };
      parsed.name = `(Copy of) ${parsed.name}`;
      const importRes = await importTemplateJson({ json_data: JSON.stringify(parsed), organization_ids: [] });
      if (importRes.status !== 201) throw new Error("import failed");
      return importRes;
    },
    onSuccess: (res) => {
      notifySuccess("Template duplicated");
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
      go(`/templates/${res.data.id}`);
    },
    onError: () => notifyError("Failed to duplicate template"),
  });

  const publishMutation = useMutation({
    mutationFn: (previousTemplateId: number | null) =>
      promoteTemplate({ template_id: numericId, previous_template_id: previousTemplateId ?? undefined }),
    onSuccess: () => {
      notifySuccess("Template published");
      setPublishDialogOpen(false);
      void refetch();
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: () => notifyError("Failed to publish template"),
  });

  const handleExportJson = async () => {
    const res = await exportTemplateJson(numericId, { cache: "no-store" });
    if (res.status === 200) {
      const jsonStr = (res.data as unknown as { json_data: string })?.json_data ?? "";
      await navigator.clipboard.writeText(jsonStr);
      notifySuccess("Template JSON copied to clipboard");
    } else {
      notifyError("Failed to export template");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-full px-16 pt-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-bg-disabled rounded w-64" />
          <div className="h-4 bg-bg-disabled rounded w-48" />
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex flex-col min-h-full px-16 pt-8">
        <p className="text-text-secondary">Template not found.</p>
        <Button text="Back to Templates" appearance="text" onClick={() => go("/templates")} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="px-16 pt-8 pb-4 border-b border-border-subtle shrink-0">
        <button
          onClick={handleBackToTemplates}
          className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-secondary mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Templates
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Headline size="medium">{template.name}</Headline>
            <Badge appearance={StatusBadge[template.status] ?? "neutral"} text={template.status} />
            {template.is_base_template && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                BASE
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeTab === TemplateTab.Details && !isEditing && (
              <Button
                text="Edit"
                appearance="secondary"
                onClick={startEdit}
                disabled={template.status === TemplateStatus.PUBLISHED}
              />
            )}
            <Button
              text="Duplicate"
              appearance="secondary"
              onClick={() => duplicateMutation.mutate()}
              disabled={duplicateMutation.isPending || hasUnsavedChanges}
            />
            <Button text="Export JSON" appearance="secondary" onClick={handleExportJson} disabled={hasUnsavedChanges} />
            {template.status === TemplateStatus.DRAFT && (
              <Button
                text="Publish"
                appearance="primary"
                onClick={() => setPublishDialogOpen(true)}
                disabled={hasUnsavedChanges}
              />
            )}
            {template.status === TemplateStatus.DRAFT && (
              <button
                onClick={() => setDeleteDialogOpen(true)}
                disabled={hasUnsavedChanges}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-16 border-b border-border-subtle shrink-0">
        <div className="flex gap-6">
          {(Object.values(TemplateTab) as TemplateTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-limestone-900 text-limestone-900"
                  : "border-transparent text-text-secondary hover:text-text-secondary"
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-16 py-6 flex-1 overflow-auto">
        {activeTab === TemplateTab.Details && (
          <TemplateDetails
            template={template}
            isEditing={isEditing}
            editName={editName}
            editDescription={editDescription}
            editStatus={editStatus}
            editReportType={editReportType}
            editExportFramework={editExportFramework}
            editTemplateUrl={editTemplateUrl}
            editHeaderText={editHeaderText}
            editFooterText={editFooterText}
            editFont={editFont}
            editUnifiedExport={editUnifiedExport}
            editTemplateType={editTemplateType}
            editRequireAllQaResolved={editRequireAllQaResolved}
            editRequireApproval={editRequireApproval}
            editFinalApproverWaits={editFinalApproverWaits}
            urlError={urlError}
            onChangeName={markDirty(setEditName)}
            onChangeDescription={markDirty(setEditDescription)}
            onChangeStatus={markDirty(setEditStatus)}
            onChangeReportType={(v: string) => {
              setDetailsDirty(true);
              setEditReportType(v);
              // report-type change invalidates the prior default pair
              setEditDefaultPayorTemplate(false);
            }}
            onChangeExportFramework={(v: string) => {
              setDetailsDirty(true);
              setEditExportFramework(v);
              // Keep unified export in lockstep with the framework. Only carry the
              // saved value forward if the template was already DOCXTEMPLATER — a
              // template arriving from another framework has no migrated .docx yet,
              // so it must not silently start checked (matches ReportTemplate.save).
              const savedDocxUnifiedExport =
                template?.export_framework === ExportFramework.DOCXTEMPLATER
                  ? (template?.unified_export ?? false)
                  : false;
              setEditUnifiedExport(resolveUnifiedExport(v, savedDocxUnifiedExport));
              setUrlError("");
            }}
            onChangeTemplateUrl={(v: string) => {
              setDetailsDirty(true);
              setEditTemplateUrl(v);
              setUrlError("");
            }}
            onChangeHeaderText={markDirty(setEditHeaderText)}
            onChangeFooterText={markDirty(setEditFooterText)}
            onChangeFont={markDirty(setEditFont)}
            onChangeUnifiedExport={markDirty(setEditUnifiedExport)}
            onChangeTemplateType={markDirty(setEditTemplateType)}
            onChangeRequireAllQaResolved={markDirty(setEditRequireAllQaResolved)}
            onChangeRequireApproval={markDirty(setEditRequireApproval)}
            onChangeFinalApproverWaits={markDirty(setEditFinalApproverWaits)}
            editPayorType={editPayorType}
            editDefaultPayorTemplate={editDefaultPayorTemplate}
            onChangePayorType={(v: PayorTypeEnum | null) => {
              setDetailsDirty(true);
              setEditPayorType(v);
              // any payor change invalidates a prior default — require re-confirm for the new pair
              setEditDefaultPayorTemplate(false);
            }}
            onChangeDefaultPayorTemplate={(v: boolean) => {
              setDetailsDirty(true);
              setEditDefaultPayorTemplate(v);
            }}
            onEditOrgs={() => setEditOrgsOpen(true)}
            onOrganizationFolderSaved={() => {
              void refetch();
              void queryClient.invalidateQueries({ queryKey: ["templates"] });
            }}
            onLogoChanged={() => {
              preserveExportFrameworkRef.current = true;
              void refetch();
            }}
            onExportFileUploaded={() => {
              preserveExportFrameworkRef.current = true;
              void refetch();
            }}
            onExportSettingsSave={saveExportSettings}
          />
        )}
        {activeTab === TemplateTab.Structure && (
          <TemplateStructure
            sections={template.sections ?? []}
            reportType={template.report_type ?? ""}
            templateId={numericId}
            onDirtyChange={setStructureDirty}
            treatmentPlanTemplate={template.treatment_plan_template ?? null}
            currentCurriculum={template.curriculum ?? null}
            organizations={template.organizations ?? []}
            lastUpdatedDate={template.last_updated_date}
            isReadOnly={template.status === TemplateStatus.PUBLISHED}
            onSaved={() => void refetch()}
            exportFramework={template.export_framework ?? undefined}
          />
        )}
        {activeTab === TemplateTab.QACriteria && (
          <TemplateQACriteria templateId={numericId} templateName={template.name} />
        )}
        {activeTab === TemplateTab.QARules && (
          <TemplateQARulesTab templateId={numericId} organizations={template.organizations} />
        )}
        {activeTab === TemplateTab.PromptSandbox && (
          <div className="relative">
            <TemplatePromptSandbox template={template} />
          </div>
        )}
      </div>

      {/* Unsaved Changes Warning */}
      <Dialog
        title="Leave without saving?"
        open={unsavedWarningOpen}
        setOpen={setUnsavedWarningOpen}
        primaryButton={{ text: "Leave without saving", onClick: handleConfirmLeave }}
        secondaryButton={{ text: "Stay", onClick: handleCancelLeave }}
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          You have unsaved changes in the Structure tab. If you leave now, your changes will be lost.
        </p>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        title={`Delete "${template.name}"?`}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        primaryButton={{
          text: "Delete",
          onClick: () => deleteMutation.mutate(),
          disabled: deleteMutation.isPending,
        }}
        secondaryButton={{ text: "Cancel", onClick: () => setDeleteDialogOpen(false) }}
        size="sm"
      >
        <p className="text-sm text-text-secondary">This will permanently delete the template. This cannot be undone.</p>
      </Dialog>

      <PromptReportTypeMismatchDialog
        open={promptMismatchDialogOpen}
        mismatches={promptMismatches}
        reportType={editReportType}
        onConfirm={() => {
          setPromptMismatchDialogOpen(false);
          updateMutation.mutate();
        }}
        onCancel={() => setPromptMismatchDialogOpen(false)}
      />

      <PublishDialog
        open={publishDialogOpen}
        setOpen={setPublishDialogOpen}
        templateName={template.name}
        reportType={template.report_type ?? null}
        currentTemplateId={numericId}
        isPending={publishMutation.isPending}
        onConfirm={(previousTemplateId) => publishMutation.mutate(previousTemplateId)}
        onCancel={() => setPublishDialogOpen(false)}
      />

      {/* Edit Organizations Dialog */}
      <AssignOrgsDialog
        templateId={numericId}
        templateName={template.name}
        currentOrgs={template.organizations ?? []}
        open={editOrgsOpen}
        onClose={() => setEditOrgsOpen(false)}
        onSaved={() => {
          void refetch();
          void queryClient.invalidateQueries({ queryKey: ["templates"] });
        }}
      />

      {activeTab === TemplateTab.Details && isEditing && (
        <TemplateSaveFooter
          isDirty={detailsDirty}
          isSaving={updateMutation.isPending}
          onSave={validateAndSave}
          onDiscard={() => {
            setIsEditing(false);
            setDetailsDirty(false);
            setUrlError("");
          }}
          saveLabel="Save"
          discardLabel="Cancel"
          alwaysShowDiscard
        />
      )}

      {/* Rio is an editing assistant — only show it on Draft templates.
          Published / Archived templates are committed state; the server's
          M2M mutation endpoints reject changes to them anyway, so showing
          Rio here would just invite refused requests. */}
      {template.status === TemplateStatus.DRAFT && (
        <RioChatDrawer templateId={numericId} templateName={template.name} />
      )}
    </div>
  );
};
